// ─────────────────────────────────────────────────────────────────────────────
// ChangelogPage, the public release page at /changelog.
//
// The product shipped as one build, so there is no dated release history to
// publish and this page does not invent one. It states plainly that dated notes
// begin with the next release, then does the useful thing instead: lists every
// capability that is live in the product today, grouped by area, so a reader
// can tell exactly what they would get on the first day.
//
// Laid out to the shared marketing kit: left-aligned headings that end in a
// period, hairline borders, one dark panel carrying the honesty notice, and a
// grey <MediaSlot> captioned with the screenshot that belongs there.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    BTN_ON_DARK,
    BTN_ON_DARK_SOFT,
    Icon,
    Container,
    Section,
    CardTitle,
    Body,
    Eyebrow,
    Pill,
    MediaSlot,
    SectionHead,
    Card,
    DarkPanel,
    Steps,
    SpecList,
    Heading,
    Lead,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
    SITE_URL,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── What is live in the product today, by area ───────────────────────────────
const AREAS = [
    {
        icon: 'clock',
        title: 'Daily close',
        note: 'One close per location per day, signed and locked.',
        items: [
            'Register totals, deposits and variances on a single day sheet',
            'A written reason is required before a short drawer can be filed',
            'Owner-only approval on the entries that need a second pair of eyes',
            'The day locks on sign-off, and later changes are on the audit log',
        ],
    },
    {
        icon: 'cash',
        title: 'Cash accountability',
        note: 'Every dollar carries a chain of custody.',
        items: [
            'Blind drawer and safe counts, with the expected figure hidden',
            'Over and short by person, by shift and by store',
            'Deposit slips recorded against what actually reached the bank',
            'Drawer, drop, safe and deposit readable as one trail',
        ],
    },
    {
        icon: 'bank',
        title: 'Reconciliation',
        note: 'Three sources matched against each other, nightly.',
        items: [
            'Bank statement import from CSV, with duplicate rows rejected',
            'Processor transactions and settlement batches through Datacap',
            'Register takings against processor batches against bank deposits',
            'An exceptions queue carrying location, source, date and amount',
        ],
    },
    {
        icon: 'wallet',
        title: 'Tenders and liabilities',
        note: 'The tenders that usually go untracked, tracked.',
        items: [
            'Refund control with mandatory reasons and approval thresholds',
            'Gift certificate liability held as a ledger, not an estimate',
            'Coupon builder, and EBT and SNAP kept as their own ledger',
            'QuickBooks journal entries exported as a file your bookkeeper imports',
        ],
    },
    {
        icon: 'chart',
        title: 'Sales and customers',
        note: 'Counter and online orders in the same tables.',
        items: [
            'Revenue by channel, location, product, hour, staff member and customer',
            'Repeat rate and lifetime value on the customer record',
            'Campaign attribution that joins analytics traffic to real orders',
            'Shipping and delivery map, holiday lift, orders down to the line item',
        ],
    },
    {
        icon: 'boxes',
        title: 'Inventory and purchasing',
        note: 'On hand derived from movements, never stored.',
        items: [
            'An append-only movement ledger you can read back line by line',
            'Reorder points and PAR, on a board that prefills a purchase order',
            'Vendors, storage locations and a catalogue with price history',
            'iPad receiving with a 41°F temperature check, variances and sign-off',
        ],
    },
    {
        icon: 'printer',
        title: 'Production and labels',
        note: 'Every scale label becomes a production record.',
        items: [
            'Production rolled up by product, by scale and by staff member',
            'Scale label format presets pushed out from the office',
            'Shelf labels and a barcode format designer',
            'Raw materials, with yield and waste tracked through to finished pack',
        ],
    },
    {
        icon: 'receipt',
        title: 'Wholesale',
        note: 'Invoicing that does not need a second system.',
        items: [
            'Invoices raised automatically from storefront orders',
            'Ten price levels as a percentage off retail, with per-customer overrides',
            'Partial payments, and credit memos so an invoice is credited, never deleted',
            'Receivables aged, and branded invoice PDFs',
        ],
    },
    {
        icon: 'phone',
        title: 'Devices and staff',
        note: 'The floor, visible from the office.',
        items: [
            'Per-app health dashboards and remote configuration',
            'Push notifications to store staff, order locks and shared notes',
            'Live driver map while a local delivery run is out',
            'Staff PINs, per-app access and single-use codes that die on first use',
        ],
    },
    {
        icon: 'shield',
        title: 'Platform',
        note: 'The parts every other module leans on.',
        items: [
            'Roles across 20 modules with 8 presets, collaborator invites and view-as',
            'A permanent audit log of every manual change',
            'Multiple workspaces with hard state isolation between them',
            'Branded CSV, Excel and PDF export from every table',
        ],
    },
];

const APPS = ['Register', 'Scale', 'Packing', 'Receiving', 'Delivery'];

// A bordered card for one area: glyph, title, one line, then the detail.
function AreaCard({ icon, title, note, items }) {
    return (
        <Card className="flex h-full flex-col gap-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[#0A0A0A]">
                <Icon name={icon} className="h-[18px] w-[18px]" />
            </span>
            <div>
                <CardTitle>{title}</CardTitle>
                <Body className="mt-2">{note}</Body>
            </div>
            <ul className="mt-auto flex flex-col gap-2.5 pt-2">
                {items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[13.5px] leading-[1.55] text-[#3D3D3D]">
                        <span className="mt-[3px] flex h-[16px] w-[16px] flex-none items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                            <Icon name="check" className="h-2.5 w-2.5" strokeWidth={2.4} />
                        </span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </Card>
    );
}

export default function ChangelogPage() {
    return (
        <MarketingShell>
            <Seo
                title="Changelog: what is live in Wavelength today"
                description="Everything live in Wavelength today, by area: daily close, cash control, reconciliation, inventory, production and wholesale. Dated notes start next release."
                path="/changelog"
                keywords="Wavelength changelog, release notes, back office product updates, daily close software, cash reconciliation features, retail inventory ledger, wholesale invoicing features"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': `${SITE_URL}/changelog`,
                            url: `${SITE_URL}/changelog`,
                            name: 'Changelog',
                            description: 'The capabilities that are live in Wavelength today, grouped by area. Dated release notes begin with the next release.',
                            publisher: { '@id': `${SITE_URL}/#organization` },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/overview` },
                                { '@type': 'ListItem', position: 2, name: 'Changelog', item: `${SITE_URL}/changelog` },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Changelog"
                title="What is live in Wavelength today."
                lead="Wavelength shipped as one build rather than a run of releases, so there is no dated history to publish yet. Here is the honest version: everything that is in the product right now, grouped by the area it belongs to."
                primary={{ label: 'Book a walkthrough', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <div className="flex flex-wrap gap-2">
                    {AREAS.map((a, i) => <Pill key={a.title} active={i === 0}>{a.title}</Pill>)}
                </div>
            </PageHero>

            {/* ── The honesty notice, on the one dark panel ───────────────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <DarkPanel className="p-6 sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
                                <div className="flex flex-col gap-5">
                                    <Eyebrow className="!text-white/45">Before you scroll</Eyebrow>
                                    <h2 className="max-w-[520px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        Dated notes start with the next release.
                                    </h2>
                                    <p className="max-w-[520px] text-[15px] leading-[1.65] text-white/60">
                                        Other changelogs open with two years of entries. This one does not, because those entries would have to be written after the fact, and a release note invented later is worth nothing to the person reading it. From the next release onward, every change gets its own dated entry on this page.
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <Link to="/features" className={BTN_ON_DARK}>Everything in the platform</Link>
                                        <Link to="/contact" className={BTN_ON_DARK_SOFT}>Ask what is next</Link>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Screenshot"
                                        label="A release note open in the workspace"
                                        note="The in-product panel a dated entry will also appear in"
                                        ratio="3 / 4"
                                        className="!rounded-[24px] max-w-[300px]"
                                    />
                                </div>
                            </div>
                        </DarkPanel>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Everything that is live ────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Live in the product now."
                            lead="Ten areas, all of them included in every plan. Nothing on this list is on a roadmap, behind a flag, or waiting on a call with sales."
                            actions={<Link to="/features" className={BTN_SECONDARY}>See it in detail<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {AREAS.map((a) => <AreaCard key={a.title} {...a} />)}
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-8 flex flex-col gap-4 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                            <div className="max-w-[420px]">
                                <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">Five companion apps, included</div>
                                <Body className="mt-1.5">They feed the same workspace, so what happens on the floor is in the numbers before anyone opens a laptop.</Body>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {APPS.map((a) => <Pill key={a}>{a}</Pill>)}
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── How releases will be published ─────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[360px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>How releases will be published.</Heading>
                                <Lead>The same rules the audit log follows, applied to the page that tells you what changed.</Lead>
                                <div className="mt-2">
                                    <Link to="/contact" className={BTN_SECONDARY}>Talk to us<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Steps
                                items={[
                                    { title: 'Every entry carries a date', body: 'The date the change reached production, not the date somebody wrote it up.' },
                                    { title: 'Grouped by the area it touches', body: 'Each entry names the modules it changes, so you can skip the ones you do not run.' },
                                    { title: 'Breaking changes come first', body: 'Anything that alters an export shape, a permission or a workflow is flagged at the top of the entry.' },
                                    { title: 'Entries are appended, never edited', body: 'A correction goes underneath the original. Nothing on this page is quietly rewritten later.' },
                                ]}
                            />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── What each entry will contain ───────────────────────────── */}
            <Section tone="paper2" pad="md">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4">
                                <Heading>What an entry will contain.</Heading>
                                <Lead className="max-w-[420px]">
                                    Short, specific and readable by the person who has to retrain a manager on Monday.
                                </Lead>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <SpecList
                                rows={[
                                    { label: 'Date', value: 'The day it reached production' },
                                    { label: 'Area', value: 'The modules it touches' },
                                    { label: 'Change', value: 'What is different now' },
                                    { label: 'Impact', value: 'What you have to do, if anything' },
                                    { label: 'Notice', value: 'Breaking changes called out first' },
                                ]}
                            />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="See it on your own numbers."
                body="Everything on this page is live today. Connect one location, close a day, and read the first reconciliation in the morning."
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Book a walkthrough', to: '/contact' }}
            />
        </MarketingShell>
    );
}
